<template>
  <div class="overflow-hidden bg-white shadow sm:rounded-lg" v-if='vars'>
    <div class="px-4 py-6 sm:px-6">
      <h2 class="text-base/7 font-semibold text-gray-900 text-xl">{{ vars.objective.name }}</h2>
      <p class="mt-1 max-w-2xl text-sm/6 text-gray-500">{{ vars.objective.objective }}</p>
    </div>
    <div class="border-t border-gray-100">
      <dl class="divide-y divide-gray-100">
        <div class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
          <dt class="text-sm font-medium text-gray-900">Question</dt>
          <dd class="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0"><pre class='whitespace-pre-wrap font-sans'>{{ vars.objective.question }}</pre></dd>
        </div>
        <div class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
          <dt class="text-sm font-medium text-gray-900">Guidance Notes</dt>
          <dd class="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0"><pre class='whitespace-pre-wrap font-sans'>{{ vars.guidanceNotes }}</pre></dd>
        </div>
        <div class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
          <dt class="text-sm font-medium text-gray-900">Score</dt>
          <dd class="mt-3 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0">
            <fieldset aria-label="Score">
              <div class="space-y-5">
                <div v-for="score in vars.scoreOptions" :key="score.score" class="relative flex items-start">
                  <div class="flex h-6 items-center">
                    <input :disabled='!vars.isDraft' v-model="props.state.response.score" :value='score.score' :id="`${score.score}-score-input`" :aria-describedby="`${score.score}-score-description`" name="plan" type="radio" class="relative size-4 appearance-none rounded-full border border-gray-300 bg-white before:absolute before:inset-1 before:rounded-full before:bg-white checked:border-indigo-600 checked:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:border-gray-300 disabled:bg-gray-100 disabled:before:bg-gray-400 forced-colors:appearance-auto forced-colors:before:hidden [&:not(:checked)]:before:hidden cursor-pointer" />
                  </div>
                  <div class="ml-3 text-sm/6">
                    <label :for="`${score.score}-score-input`" class='rounded-full self-start w-20 h-5 flex items-center justify-center shrink-0 text-xs font-medium cursor-pointer' :class="[score.bgHigh, score.bgText]">{{ score.label }}</label>
                    <pre :id="`${score.score}-score-description`" class='whitespace-pre-wrap font-sans mt-1'>{{ score.description }}</pre>
                  </div>
                </div>
              </div>
            </fieldset>
          </dd>
        </div>
        <div class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
          <dt class="text-sm font-medium text-gray-900"><label for='notes'>Evidence and Next Steps</label></dt>
          <dd class="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0">
            <TextareaInput labelId='notes' :hideLabel='true' v-model='props.state.response.notes' :disabled='!vars.isDraft' placeholder='Enter here...' />
          </dd>
        </div>
        <div class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
          <dt class="text-sm font-medium text-gray-900">Stewardship Uplift</dt>
          <dd class="mt-3 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0 space-y-5">
            <div v-for='progression in vars.upliftGuide'>
              <h3 class='flex gap-2 items-center'>
                <span class='rounded-full w-20 h-5 flex items-center justify-center shrink-0 text-xs font-medium' :class="[progression.from.bgHigh, progression.from.bgText]">{{ progression.from.label }}</span>
                <span class='text-xs font-medium'>to</span>
                <span class='rounded-full w-20 h-5 flex items-center justify-center shrink-0 text-xs font-medium' :class="[progression.to.bgHigh, progression.to.bgText]">{{ progression.to.label }}</span>
              </h3>
              <pre class='whitespace-pre-wrap font-sans mt-1'>{{ progression.description }}</pre>
            </div>
          </dd>
        </div>
      </dl>
    </div>
  </div>
</template>

<script setup>

import { computed } from 'vue';

import { RadioGroup, RadioGroupOption } from '@headlessui/vue';

import TextareaInput from '@/components/inputs/textarea-input.vue';

import { scoreMap } from '@/kernel/scoring';

const props = defineProps({
  state: Object
});

const vars = computed(() => {
  const objective = props.state.response.objective;

  if (!objective) {
    return null;
  }

  const guidanceNotes = objective.guidanceNotes.replaceAll('\n', '\n\n');

  const scoreOptions = [
    { score: 0, description: objective.scoreGuideA },
    { score: -1, description: objective.scoreGuideB },
    { score: -2, description: objective.scoreGuideC },
    { score: -3, description: objective.scoreGuideD }
  ];

  scoreOptions.forEach((option) => {
    option.label = scoreMap.getLabel(option.score);
    option.bgHigh = scoreMap.getColour(option.score, 'bg', 'high');
    option.bgLow = scoreMap.getColour(option.score, 'bg', 'low');
    option.bgText = scoreMap.getColour(option.score, 'bg', 'text');
    option.borderHigh = scoreMap.getColour(option.score, 'border', 'high');
    option.borderLow = scoreMap.getColour(option.score, 'border', 'low');
  });

  const upliftGuide = [
    {from: -3, to: -2},
    {from: -2, to: -1},
    {from: -1, to: 0}
  ].map((pair) => {
    const progression = {
      description: objective[`upliftGuide${scoreMap.getLetter(pair.from)}to${scoreMap.getLetter(pair.to)}`]
    }

    Object.keys(pair).forEach((key) => {
      progression[key] = {
        label: scoreMap.getLabel(pair[key]),
        bgHigh: scoreMap.getColour(pair[key], 'bg', 'high'),
        bgLow: scoreMap.getColour(pair[key], 'bg', 'low'),
        bgText: scoreMap.getColour(pair[key], 'bg', 'text')
      }
    });

    return progression;
  });

  return {
    objective,
    guidanceNotes,
    scoreOptions,
    upliftGuide,
    isDraft: props.state.status === 'draft'
  };
});

</script>
